import catalogue from './studyToolCatalogue.generated.json';
import { api, ApiError, refreshAccessToken } from './api';
import { getKidSub, useAuthStore } from '@/auth/authStore';
import { observeToolSurface } from './studyToolControls.generated';

export const TELEMETRY_TTL_MS = 86400000;
export const TELEMETRY_IDLE_MS = 60000;
const PREFIX = 'airbotix.study-tool-usage.v2:';
const MAX_QUEUE = 200;
const MAX_CACHE_BYTES = 250000;
export const USAGE_ACTIONS = ['condition_changed', 'control_activated', 'simulation_manipulated', 'formula_selected', 'canvas_edited', 'annotation_added', 'tutor_submitted', 'practice_requested', 'answer_submitted', 'review_opened'] as const;
export function isUsageAction(value: string | null): value is UsageAction { return USAGE_ACTIONS.some((action) => action === value); }
export type UsageAction = 'condition_changed' | 'control_activated' | 'simulation_manipulated' |
  'formula_selected' | 'canvas_edited' | 'annotation_added' | 'tutor_submitted' |
  'practice_requested' | 'answer_submitted' | 'review_opened';
export type UsageMode = 'explore' | 'guided' | 'practice' | 'review';
export interface UsageEvent {
  event_id: string; session_id: string; sequence: number; subject_key: string; tool_key: string;
  collector_version: 'host-v2'; kind: string; occurred_at: string; view_year: number | 'all' | null;
  mode: UsageMode; action_key?: UsageAction; correct?: boolean;
  error_code?: 'load_failed';
  active_ranges: { start: string; end: string }[];
}
export interface RegisteredTool { subject_key: string; tool_key: string; name: string; routes: string[]; aliases: string[]; family: string; release_status: string; delivery: string; coverage: string; source_dirty: boolean }
export const registeredTools: RegisteredTool[] = catalogue.tools;
export function registeredRoute(path: string) {
  return registeredTools.find((tool) => tool.routes.some((route) => route === path ||
    (route.endsWith('/:id') && path.startsWith(route.slice(0, -3)) && path !== route.slice(0, -4))));
}
export function registeredKey(subject: string, key: string) {
  return registeredTools.find((tool) => tool.subject_key === subject && (tool.tool_key === key || tool.aliases.includes(key)));
}

interface Visit { tool: RegisteredTool; id: string; sequence: number; opened: boolean; activeAt: number; lastAction: number; year: number | 'all' | null; mode: UsageMode; lastKey?: UsageAction }
interface Ack { results: { event_id: string; status: 'accepted' | 'duplicate' | 'rejected'; code?: string }[] }
interface CollectorOptions {
  send?: typeof api; owner?: () => string | null; clock?: () => number;
  visible?: () => boolean; focused?: () => boolean; refresh?: () => Promise<unknown>;
  contextMode?: () => UsageMode | undefined;
}

/** One owner-bound queue for all Study tools. It contains no token, URL, answer or document content. */
export class StudyToolCollector {
  private readonly send: typeof api;
  private readonly currentOwner: () => string | null;
  private readonly now: () => number;
  private readonly visible: () => boolean;
  private readonly focused: () => boolean;
  private readonly refresh: () => Promise<unknown>;
  private readonly contextMode: () => UsageMode | undefined;
  private owner: string | null;
  private enabled = false;
  private stopped = false;
  private storageKey: string | null = null;
  private pending: UsageEvent[] = [];
  private visits = new Map<string, Visit>();
  private sending = false;
  private refreshAttempts = 0;
  private clockOffset = 0;
  private retry: ReturnType<typeof setTimeout> | undefined;
  private controller = new AbortController();
  private unsubscribe: () => void;
  readonly ready: Promise<void>;

  constructor(options: CollectorOptions = {}) {
    this.send = options.send ?? api;
    this.currentOwner = options.owner ?? getKidSub;
    const wall = Date.now(), monotonic = performance.now();
    this.now = options.clock ?? (() => wall + performance.now() - monotonic + this.clockOffset);
    this.visible = options.visible ?? (() => document.visibilityState === 'visible');
    this.focused = options.focused ?? (() => document.hasFocus());
    this.refresh = options.refresh ?? (() => refreshAccessToken('kid'));
    this.contextMode = options.contextMode ?? (() => undefined);
    this.owner = this.currentOwner();
    this.unsubscribe = useAuthStore.subscribe(() => { if (this.currentOwner() !== this.owner) this.stop(true); });
    this.ready = this.initialize();
  }
  private active() { return !this.stopped && !!this.owner && this.currentOwner() === this.owner; }
  private async initialize() {
    if (!this.owner) return;
    try {
      const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(this.owner));
      if (!this.active()) return;
      this.storageKey = PREFIX + [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
      for (const key of Object.keys(localStorage)) if (key.startsWith(PREFIX) && key !== this.storageKey) localStorage.removeItem(key);
    } catch { /* Owner binding still applies when browser storage is denied. */ }
    try {
      const began = performance.now();
      const result = await this.send<{ enabled: boolean; server_time?: string }>('/study/tools/telemetry', { principal: 'kid', signal: this.controller.signal, skipAuthRefresh: true });
      if (!this.active()) return;
      if (result.server_time && Number.isFinite(+new Date(result.server_time)))
        this.clockOffset = +new Date(result.server_time) + (performance.now() - began) / 2 - this.now();
      this.enabled = result.enabled;
      if (!this.enabled) { this.pending = []; this.persist(); return; }
      try {
        const text = this.storageKey ? localStorage.getItem(this.storageKey) ?? '[]' : '[]';
        const stored: unknown = text.length <= MAX_CACHE_BYTES ? JSON.parse(text) : [];
        if (Array.isArray(stored)) this.pending = stored.filter((event): event is UsageEvent => this.validCached(event)).slice(-MAX_QUEUE);
      } catch { /* Invalid cache is replaced; it never prevents in-memory collection. */ }
      this.persist();
    } catch { this.enabled = false; }
  }
  private validCached(event: unknown): event is UsageEvent {
    if (!event || typeof event !== 'object') return false;
    const row = event as UsageEvent;
    const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuid.test(row.event_id ?? '') && uuid.test(row.session_id ?? '') && Number.isInteger(row.sequence) && row.sequence >= 0 && row.sequence <= 100000 &&
      row.collector_version === 'host-v2' && registeredKey(row.subject_key, row.tool_key)?.tool_key === row.tool_key &&
      ['tool_opened','tool_interacted','session_progress','session_ended','prediction_answered','check_answered','tool_error'].includes(row.kind) &&
      ['explore','guided','practice','review'].includes(row.mode) &&
      (row.view_year === null || row.view_year === 'all' || (Number.isInteger(row.view_year) && Number(row.view_year) >= 6 && Number(row.view_year) <= 12)) &&
      (row.error_code === undefined || row.error_code === 'load_failed') &&
      (row.action_key === undefined || isUsageAction(row.action_key)) && (row.correct === undefined || typeof row.correct === 'boolean') &&
      +new Date(row.occurred_at) >= this.now() - TELEMETRY_TTL_MS && +new Date(row.occurred_at) <= this.now() + 300000 &&
      Array.isArray(row.active_ranges) && row.active_ranges.length <= 20 && row.active_ranges.every((range) =>
        range && Object.keys(range).every((key) => key === 'start' || key === 'end') &&
        Number.isFinite(+new Date(range.start)) && +new Date(range.end) > +new Date(range.start) && +new Date(range.end) - +new Date(range.start) <= 30000) &&
      Object.keys(row).every((key) => ['event_id','session_id','sequence','subject_key','tool_key',
        'collector_version','kind','occurred_at','view_year','mode','action_key','correct','error_code','active_ranges'].includes(key));
  }
  private persist() {
    if (!this.storageKey) return;
    try { if (this.pending.length) localStorage.setItem(this.storageKey, JSON.stringify(this.pending)); else localStorage.removeItem(this.storageKey); }
    catch { /* Denied storage leaves the bounded in-memory queue available. */ }
  }
  visit(tool: RegisteredTool, year: number | 'all' | null) {
    const key = `${tool.subject_key}:${tool.tool_key}`;
    let visit = this.visits.get(key);
    if (!visit) {
      visit = { tool, id: crypto.randomUUID(), sequence: 0, opened: false, activeAt: 0, lastAction: 0, year, mode: this.contextMode() ?? toolMode(tool) };
      this.visits.set(key, visit);
    } else if (year !== null && visit.year !== year) {
      this.progress(visit, true); visit.year = year;
    }
    return visit;
  }
  async open(tool: RegisteredTool, year: number | 'all' | null) {
    const visit = this.visit(tool, year);
    await this.ready;
    if (!this.active() || !this.enabled || this.visits.get(`${tool.subject_key}:${tool.tool_key}`) !== visit || visit.opened || tool.release_status === 'candidate') return;
    visit.opened = this.enqueue(visit, 'tool_opened');
  }
  async interact(tool: RegisteredTool, year: number | 'all' | null, action: UsageAction, answer?: { kind: 'prediction_answered' | 'check_answered'; correct?: boolean }) {
    await this.open(tool, year);
    const visit = this.visit(tool, year);
    if (!this.active() || !this.enabled || !visit.opened) return;
    if (!answer && visit.lastKey === action && this.now() - visit.lastAction < 500) return;
    visit.lastKey = action;
    this.progress(visit);
    const now = this.now();
    visit.lastAction = now;
    if (this.visible() && this.focused()) visit.activeAt = now;
    this.enqueue(visit, answer?.kind ?? 'tool_interacted', answer ? { correct: answer.correct } : { action_key: action });
  }
  private enqueue(visit: Visit, kind: string, extra: Partial<UsageEvent> = {}) {
    if (!this.active() || !this.enabled) return false;
    this.pending = this.pending.filter((e) => +new Date(e.occurred_at) >= this.now() - TELEMETRY_TTL_MS);
    if (this.pending.length >= MAX_QUEUE) {
      console.error('[study-tool-usage] queue full', { code: 'STUDY_TOOL_USAGE_QUEUE_FULL' });
      return false;
    }
    const event: UsageEvent = { event_id: crypto.randomUUID(), session_id: visit.id, sequence: visit.sequence,
      subject_key: visit.tool.subject_key, tool_key: visit.tool.tool_key, collector_version: 'host-v2', kind,
      occurred_at: new Date(this.now()).toISOString(), view_year: visit.year, mode: visit.mode, active_ranges: [], ...extra };
    if (JSON.stringify([...this.pending, event]).length > MAX_CACHE_BYTES) {
      console.error('[study-tool-usage] queue full', { code: 'STUDY_TOOL_USAGE_QUEUE_FULL' }); return false;
    }
    this.pending.push(event); visit.sequence++;
    this.persist(); this.schedule(0); return true;
  }
  private progress(visit: Visit, pause = false) {
    if (visit.activeAt && visit.opened) {
      const end = Math.min(this.now(), visit.lastAction + TELEMETRY_IDLE_MS);
      const ranges = [];
      for (let start = visit.activeAt; start < end; start += 30000)
        ranges.push({ start: new Date(start).toISOString(), end: new Date(Math.min(start + 30000, end)).toISOString() });
      if (ranges.length) this.enqueue(visit, 'session_progress', { active_ranges: ranges });
      visit.activeAt = !pause && this.visible() && this.focused() && end < visit.lastAction + TELEMETRY_IDLE_MS ? end : 0;
    }
  }
  async failure(tool: RegisteredTool) {
    await this.ready;
    if (!this.active() || !this.enabled || tool.release_status === 'candidate') return;
    const diagnostic: Visit = { tool, id: crypto.randomUUID(), sequence: 0, opened: false, activeAt: 0, lastAction: 0, year: null, mode: this.contextMode() ?? toolMode(tool) };
    this.enqueue(diagnostic, 'tool_error', { error_code: 'load_failed' });
  }
  continueReading(tool: RegisteredTool) {
    const visit = this.visits.get(`${tool.subject_key}:${tool.tool_key}`);
    if (!visit?.opened || !visit.lastAction || tool.tool_key !== 'formulas' || !this.visible() || !this.focused()) return;
    this.progress(visit); visit.lastAction = this.now(); visit.activeAt ||= this.now();
  }
  tick(pause = false) { for (const visit of this.visits.values()) this.progress(visit, pause); }
  close(tool?: RegisteredTool) {
    for (const [key, visit] of this.visits) {
      if (tool && visit.tool !== tool) continue;
      this.progress(visit, true);
      if (visit.opened) this.enqueue(visit, 'session_ended');
      this.visits.delete(key);
    }
  }
  private schedule(delay: number) {
    clearTimeout(this.retry);
    if (this.active() && this.pending.length) this.retry = setTimeout(() => { void this.flush(); }, delay);
  }
  async flush() {
    await this.ready;
    if (!this.active() || !this.enabled || this.sending || !this.pending.length) return;
    this.sending = true;
    const batch = this.pending.slice(0, 50);
    try {
      const ack = await this.send<Ack>('/study/tools/events/v2', { method: 'POST', principal: 'kid',
        skipAuthRefresh: true, signal: this.controller.signal, body: { events: batch } });
      if (!this.active()) return;
      this.refreshAttempts = 0;
      const ids = new Set(ack.results.filter((r) => batch.some((e) => e.event_id === r.event_id)).map((r) => r.event_id));
      this.pending = this.pending.filter((e) => !ids.has(e.event_id));
      for (const rejected of ack.results.filter((r) => r.status === 'rejected'))
        console.error('[study-tool-usage] rejected', { code: rejected.code, event_id: rejected.event_id });
      this.persist();
    } catch (error) {
      if (!this.active()) return;
      if (error instanceof ApiError && error.status === 401) {
        if (this.refreshAttempts++ > 0) this.stop(true);
        else { try { await this.refresh(); } catch { this.stop(true); } if (!this.active()) this.stop(true); }
      }
      else if (error instanceof ApiError && [400, 403, 409].includes(error.status)) this.stop(true);
    } finally { this.sending = false; this.schedule(5000); }
  }
  stop(clear = false) {
    this.stopped = true; this.controller.abort(); clearTimeout(this.retry); this.unsubscribe();
    this.visits.clear();
    if (clear) { this.pending = []; this.persist(); }
  }
}

function toolMode(tool: RegisteredTool): UsageMode {
  return tool.tool_key === 'practice-generation' || tool.tool_key === 'practice-answer-review' ? 'practice' : tool.tool_key === 'study-tutor' ? 'guided' : 'explore';
}

let hostedCollector: StudyToolCollector | null = null;
let hostedTool: RegisteredTool | null = null;
export function setHostedTool(tool: RegisteredTool | null) { hostedTool = tool; }
export function setHostedCollector(collector: StudyToolCollector | null) { hostedCollector = collector; if (!collector) hostedTool = null; }
export function hostedUsageBinding(subject: string, key: string) {
  const usage = hostedCollector, tool = registeredKey(subject, key), owner = getKidSub();
  if (!usage || !tool || tool !== hostedTool) return null;
  return { usage, tool, current: () => usage === hostedCollector && tool === hostedTool && owner === getKidSub() };
}
/** Bind after a module mounts. Shadow-root listeners also receive its non-composed change events. */
export function observeHostedToolSurface(host: HTMLElement) {
  const tool = hostedTool;
  const binding = tool && hostedUsageBinding(tool.subject_key, tool.tool_key);
  if (!binding) return () => undefined;
  host.dataset.studySurface = 'true';
  const detach = observeToolSurface(host,
    () => { if (binding.current()) void binding.usage.open(binding.tool, null); },
    (action) => { if (binding.current()) void binding.usage.interact(binding.tool, null, action); });
  return () => { detach(); delete host.dataset.studySurface; };
}
/** Keep a module's delayed events attached to the owner and route that created it. */
export function captureToolUsageBridge(subject: string) {
  const usage = hostedCollector, tool = hostedTool, owner = getKidSub();
  return (event: { tool_key: string; kind: string; correct?: boolean }) => {
    if (!usage) return false;
    if (usage !== hostedCollector || tool !== hostedTool || owner !== getKidSub() || registeredKey(subject, event.tool_key) !== tool) return true;
    return bridgeToolUsage(subject, event);
  };
}
export function bridgeToolUsage(subject: string, event: { tool_key: string; kind: string; correct?: boolean }) {
  const tool = registeredKey(subject, event.tool_key);
  if (!hostedCollector || !tool) return false;
  if (event.kind === 'tool_used') void hostedCollector.open(tool, null);
  else if (event.kind === 'prediction_answered' || event.kind === 'check_answered')
    void hostedCollector.interact(tool, null, 'answer_submitted', { kind: event.kind, correct: event.correct });
  return true;
}

export function recordHostedInteraction(key: string, action: UsageAction) {
  const tool = registeredKey('study', key);
  if (hostedCollector && tool) void hostedCollector.interact(tool, null, action);
}
export function recordHostedPrediction(correct: boolean) {
  const tool = hostedTool;
  if (hostedCollector && tool) void hostedCollector.interact(tool, null, 'answer_submitted', { kind: 'prediction_answered', correct });
}

export function recordHostedLoadFailure() {
  if (hostedCollector && hostedTool) void hostedCollector.failure(hostedTool);
}
